'use client';

import { Button } from '@connected/ui/components/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@connected/ui/components/popover';
import {
    addHours,
    eachHourOfInterval,
    format,
    getHours,
    getMinutes,
    isSameDay,
    startOfDay,
} from 'date-fns';
import { enGB, nl } from 'date-fns/locale';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
// moved React import up to satisfy lint order

import type { CalendarEvent } from '@/components/event-calendar';

import {
    DraggableEvent,
    DroppableCell,
    EventHeight,
    EventItem,
    useCurrentTimeIndicator,
    WeekCellsHeight,
} from '@/components/event-calendar';
import { EndHour, StartHour } from '@/components/event-calendar/constants';
import { cn } from '@/lib/utils';

const allDayHeightClasses = [
    'min-h-[calc(var(--event-height)+var(--event-gap))]',
    'min-h-[calc((var(--event-height)+var(--event-gap))*2)]',
    'min-h-[calc((var(--event-height)+var(--event-gap))*3)]',
    'min-h-[calc((var(--event-height)+var(--event-gap))*4)]',
    'min-h-[calc((var(--event-height)+var(--event-gap))*5)]',
];

type DayViewProps = {
    currentDate: Date;
    events: CalendarEvent[];
    onEventSelect: (event: CalendarEvent) => void;
    onEventCreate: (startTime: Date) => void;
    selectedEventId?: string | null;
    onBackgroundClick?: () => void;
};

export function DayView({
    currentDate,
    events,
    onEventSelect,
    onEventCreate,
    selectedEventId,
    onBackgroundClick,
}: DayViewProps) {
    const { i18n, t } = useTranslation('dashboard');
    const [internalActiveEventId, setInternalActiveEventId] = useState<string | null>(null);
    const activeEventId = selectedEventId ?? internalActiveEventId;
    const dateFnsLocale = useMemo(() => {
        const lang = i18n.language || 'en-GB';
        if (lang.startsWith('nl'))
            return nl;
        return enGB;
    }, [i18n.language]);
    const hours = useMemo(() => {
        const dayStart = startOfDay(currentDate);
        return eachHourOfInterval({
            start: addHours(dayStart, StartHour),
            end: addHours(dayStart, EndHour - 1),
        });
    }, [currentDate]);

    const dayEvents = useMemo(() => {
        return events
            .filter((event) => {
                const eventStart = new Date(event.start);
                const eventEnd = new Date(event.end ?? event.start);
                return (
                    isSameDay(currentDate, eventStart)
                    || isSameDay(currentDate, eventEnd)
                    || (currentDate > eventStart && currentDate < eventEnd)
                );
            })
            .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    }, [currentDate, events]);

    // Filter only explicitly all-day events (events without specific times)
    const allDayEvents = useMemo(() => {
        return dayEvents.filter((event) => {
            // Only include explicitly marked all-day events
            return event.allDay === true;
        });
    }, [dayEvents]);

    // Get all time-based events (including multi-day events with specific times)
    const timeEvents = useMemo(() => {
        return dayEvents.filter((event) => {
            // Exclude only explicitly all-day events
            return event.allDay !== true;
        });
    }, [dayEvents]);

    // Group events by hour for stacked layout when there are multiple events
    const eventsByHour = useMemo(() => {
        const hourGroups = new Map<number, CalendarEvent[]>();

        timeEvents.forEach((event) => {
            const eventStart = new Date(event.start);

            // For multi-day events, keep original start time position on all days
            const startHour = Math.max(Math.floor(getHours(eventStart)), StartHour);

            // Only add if within visible hours
            if (startHour < EndHour) {
                if (!hourGroups.has(startHour)) {
                    hourGroups.set(startHour, []);
                }
                hourGroups.get(startHour)!.push(event);
            }
        });

        return hourGroups;
    }, [timeEvents]);

    const handleEventClick = (event: CalendarEvent, e: React.MouseEvent) => {
        e.stopPropagation();
        setInternalActiveEventId(event.id ?? null);
        onEventSelect(event);
    };

    const clearActiveSelection = () => {
        setInternalActiveEventId(null);
        onBackgroundClick?.();
    };

    const showAllDaySection = allDayEvents.length > 0;
    const allDayRowCount = Math.max(1, allDayEvents.length);
    const allDayHeightClass
        = allDayHeightClasses[Math.min(allDayRowCount - 1, allDayHeightClasses.length - 1)]
            ?? allDayHeightClasses[allDayHeightClasses.length - 1];
    const { currentTimePosition, currentTimeVisible } = useCurrentTimeIndicator(
        currentDate,
        'day',
    );

    return (
        <div data-slot="day-view" className="flex h-full flex-col" onClick={clearActiveSelection}>
            {showAllDaySection && (
                <div className="border-border/70 bg-muted/50 border-b">
                    <div className="grid grid-cols-[3rem_1fr] sm:grid-cols-[4rem_1fr]">
                        <div className={cn('border-border/70 relative border-r', allDayHeightClass)}>
                            <span className="text-muted-foreground/70 absolute bottom-0 left-0 h-6 w-16 max-w-full pe-2 text-right text-[10px] sm:pe-4 sm:text-xs">
                                {t('planning.allDay', 'No date')}
                            </span>
                        </div>
                        <div className={cn('border-border/70 relative p-1', allDayHeightClass)}>
                            <div className="space-y-[var(--event-gap)]">
                                {allDayEvents.map((event) => {
                                    const eventStart = new Date(event.start);
                                    const eventEnd = new Date(event.end ?? event.start);
                                    const isFirstDay = isSameDay(currentDate, eventStart);
                                    const isLastDay = isSameDay(currentDate, eventEnd);

                                    return (
                                        <div key={`spanning-${event.id}`} className="px-0.5">
                                            <DraggableEvent
                                                event={event}
                                                view="month"
                                                onClick={e => handleEventClick(event, e)}
                                                isFirstDay={isFirstDay}
                                                isLastDay={isLastDay}
                                                isSelected={activeEventId === event.id}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="border-border/70 grid flex-1 grid-cols-[3rem_1fr] overflow-hidden border-t sm:grid-cols-[4rem_1fr]">
                <div>
                    {hours.map((hour, index) => (
                        <div
                            key={hour.toString()}
                            className="border-border/70 relative h-[var(--week-cells-height)] border-b last:border-b-0"
                        >
                            {index > 0 && (
                                <span className="bg-background text-muted-foreground/70 absolute -top-3 left-0 flex h-6 w-16 max-w-full items-center justify-end pe-2 text-[10px] sm:pe-4 sm:text-xs">
                                    {format(hour, 'HH:mm', { locale: dateFnsLocale })}
                                </span>
                            )}
                        </div>
                    ))}
                </div>

                <div className="relative">
                    {/* Current time indicator */}
                    {currentTimeVisible && (
                        <div
                            className="pointer-events-none absolute right-0 left-0 z-20"
                            style={{ top: `${currentTimePosition}%` }}
                        >
                            <div className="relative flex items-center">
                                <div className="bg-primary absolute -left-1 h-2 w-2 rounded-full"></div>
                                <div className="bg-primary h-[2px] w-full"></div>
                            </div>
                        </div>
                    )}

                    {/* Time grid with events */}
                    {hours.map((hour) => {
                        const hourValue = getHours(hour);
                        const hourEvents = eventsByHour.get(hourValue) || [];

                        // Sort events by start time
                        const sortedHourEvents = [...hourEvents].sort((a, b) =>
                            new Date(a.start).getTime() - new Date(b.start).getTime(),
                        );

                        // For side-by-side layout with "Meer" button when 4+ events
                        const maxVisibleColumns = 3;
                        const hasOverflow = sortedHourEvents.length > maxVisibleColumns;
                        const visibleEvents = hasOverflow
                            ? sortedHourEvents.slice(0, maxVisibleColumns - 1)
                            : sortedHourEvents;
                        const remainingEvents = hasOverflow
                            ? sortedHourEvents.slice(maxVisibleColumns - 1)
                            : [];
                        const remainingCount = remainingEvents.length;

                        // Calculate width for each visible event
                        const totalColumns = hasOverflow ? maxVisibleColumns : Math.max(visibleEvents.length, 1);
                        const eventWidth = 100 / totalColumns;

                        return (
                            <div
                                key={hour.toString()}
                                className="border-border/70 relative h-[var(--week-cells-height)] border-b last:border-b-0"
                            >
                                {/* Side-by-side positioned events with fixed height */}
                                {visibleEvents.map((event, index) => {
                                    const eventStart = new Date(event.start);
                                    const eventEnd = new Date(event.end ?? event.start);
                                    const isFirstDay = isSameDay(currentDate, eventStart);
                                    const isLastDay = isSameDay(currentDate, eventEnd);

                                    // Calculate minute offset within the hour for positioning
                                    // Use original start time for all days (including continuation days)
                                    const minuteOffset = getMinutes(eventStart);
                                    const topOffset = (minuteOffset / 60) * WeekCellsHeight;

                                    return (
                                        <div
                                            key={event.id}
                                            className="absolute z-10 px-0.5"
                                            style={{
                                                top: `${topOffset}px`,
                                                height: `${EventHeight}px`,
                                                left: `${index * eventWidth}%`,
                                                width: `${eventWidth}%`,
                                                zIndex: 10 + index,
                                            }}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <div className="h-full w-full">
                                                <DraggableEvent
                                                    event={event}
                                                    view="month"
                                                    onClick={e => handleEventClick(event, e)}
                                                    isFirstDay={isFirstDay}
                                                    isLastDay={isLastDay}
                                                    isSelected={event.id === activeEventId}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* "Meer" button on the right when there are 4+ events */}
                                {remainingCount > 0 && (
                                    <div
                                        className="absolute z-20 px-0.5"
                                        style={{
                                            top: '2px',
                                            left: `${(maxVisibleColumns - 1) * eventWidth}%`,
                                            width: `${eventWidth}%`,
                                        }}
                                    >
                                        <Popover modal>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    className="focus-visible:border-ring bg-muted/80 hover:bg-muted focus-visible:ring-ring/50 text-muted-foreground hover:text-foreground flex h-full w-full items-center justify-center gap-1 rounded-md px-2 text-xs font-medium backdrop-blur-md transition outline-none select-none focus-visible:ring-2"
                                                    style={{ height: `${EventHeight}px` }}
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <span>{t('dashboard.planning.message.moreMsgs', 'Meer')}</span>
                                                    <span className="text-xs">
                                                        (
                                                        {remainingCount}
                                                        )
                                                    </span>
                                                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent
                                                align="end"
                                                side="right"
                                                className="max-w-64 p-3"
                                                style={
                                                    {
                                                        '--event-height': `${EventHeight}px`,
                                                    } as React.CSSProperties
                                                }
                                            >
                                                <div className="space-y-2">
                                                    <div className="text-sm font-medium">
                                                        {format(currentDate, 'd MMM, EEEE', { locale: dateFnsLocale })}
                                                        {' - '}
                                                        {format(hour, 'HH:mm', { locale: dateFnsLocale })}
                                                    </div>
                                                    <div className="space-y-1">
                                                        {remainingEvents.map((event) => {
                                                            const eventStart = new Date(event.start);
                                                            const eventEnd = new Date(event.end ?? event.start);
                                                            const isFirstDay = isSameDay(currentDate, eventStart);
                                                            const isLastDay = isSameDay(currentDate, eventEnd);

                                                            return (
                                                                <EventItem
                                                                    key={event.id}
                                                                    onClick={e => handleEventClick(event, e)}
                                                                    event={event}
                                                                    view="month"
                                                                    isFirstDay={isFirstDay}
                                                                    isLastDay={isLastDay}
                                                                    isSelected={event.id === activeEventId}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                )}

                                {/* Quarter-hour intervals */}
                                {[0, 1, 2, 3].map((quarter) => {
                                    const quarterHourTime = hourValue + quarter * 0.25;
                                    return (
                                        <DroppableCell
                                            key={`${hour.toString()}-${quarter}`}
                                            id={`day-cell-${currentDate.toISOString()}-${quarterHourTime}`}
                                            date={currentDate}
                                            time={quarterHourTime}
                                            className={cn(
                                                'absolute h-[calc(var(--week-cells-height)/4)] w-full',
                                                quarter === 0 && 'top-0',
                                                quarter === 1
                                                && 'top-[calc(var(--week-cells-height)/4)]',
                                                quarter === 2
                                                && 'top-[calc(var(--week-cells-height)/4*2)]',
                                                quarter === 3
                                                && 'top-[calc(var(--week-cells-height)/4*3)]',
                                            )}
                                            onClick={() => {
                                                clearActiveSelection();
                                                const startTime = new Date(currentDate);
                                                startTime.setHours(hourValue);
                                                startTime.setMinutes(quarter * 15);
                                                onEventCreate(startTime);
                                            }}
                                        />
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
